import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categoriesData, articlesData, type CategoryItem, type ArticlePreview } from '@/mocks/articles';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') || categoriesData[0].id;

  const handleCategoryChange = (catId: string) => {
    setSearchParams({ cat: catId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    document.title = `luluca ${currentCategory.name} - 分类浏览 | luluca官网`;
  }, [currentCategory]);

  const currentCategory = useMemo(
    () => categoriesData.find((c) => c.id === activeCategory) || categoriesData[0],
    [activeCategory],
  );

  const filteredArticles = useMemo(
    () => articlesData.filter((a) => a.categoryId === activeCategory),
    [activeCategory],
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `luluca ${currentCategory.name} - 分类浏览`,
    description: currentCategory.description,
    url: `https://luluca.com/categories?cat=${activeCategory}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: 'luluca 官方落地页',
      url: 'https://luluca.com/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '分类浏览' }, { label: currentCategory.name }]} />

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                luluca 内容中心
              </h1>
              <p className="text-foreground-600 text-sm md:text-base">
                浏览分类内容，发现更多 luluca 的使用技巧与创作灵感
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categoriesData.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="mb-8 p-5 md:p-6 bg-background-100 rounded-2xl">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory.name}
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {currentCategory.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}

function ArticleCard({ article }: { article: ArticlePreview }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 border border-background-200 rounded-2xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs text-foreground-500">{article.date}</span>
          <span className="text-xs text-foreground-400">·</span>
          <span className="text-xs text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="text-base font-heading font-bold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="inline-block px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}